import { useCallback, useMemo, useState, Fragment, useEffect, useRef } from "react";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReportTypeItem from "../components/report/report_type_item";
import { useGetReportCategories } from "../../domain/hooks/use_get_report_categories";
import { ReportCategoryInterface } from "../../domain/interface/report_category_interface";
import { PulseLoader, SyncLoader } from "react-spinners";
import { useReport } from "../../domain/hooks/use_report";
import { useCheckMeetingExists } from "../../domain/hooks/use_check_meeting_exists";
import CloseIcon from '../../../../assets/images/close_icon.svg?react';
import BackArrowIcon from '../../../../assets/images/back_arrow_icon.svg?react';
import { useBarColor } from "../../../../core/hooks/use_bar_color";
import { toast } from 'sonner';
import { useTranslation } from '../../../../i18n/useTranslation';

const PAGE_VARIANTS = {
    enter: (direction: number) => ({
        x: direction < 0 ? '-100%' : '100%',
    }),
    center: { x: 0 },
    exit: (direction: number) => ({
        x: direction < 0 ? '100%' : '-100%',
    })
};

const PAGE_TRANSITION = {
    type: "tween" as const,
    duration: 0.2,
};

const keys = [
    'type',
    'comment'
];

const ReportVibesPage: React.FC = () => {
    const { t } = useTranslation();
    const { sessionId, meetingId, step } = useParams();
    const theme = ThemeConstants();
    const location = useLocation();
    const fromHistory = sessionId === undefined;
    const basePath = fromHistory ? `/profile/me/report/${meetingId}` : `/vibes/${sessionId}/${meetingId}/report`;

    useBarColor('#FFFFFF');

    const [ comment, setComment ] = useState('');
    const [ opened, setOpened ] = useState<string | null>(null);
    const [ selectedReportCategory, setSelectedReportCategory ] = useState<{ category: ReportCategoryInterface, severity: number } | null>(null);
    const [ currentStep, setCurrentStep ] = useState(0);
    const [ direction, setDirection ] = useState(1);
    const { data: reportCategories, isLoading: isGettingCategories, error: categoriesError, refetch } = useGetReportCategories();
    const navigate = useNavigate();
    const { mutate: report, isPending: isReporting } = useReport();
    const { error: meetingError } = useCheckMeetingExists(meetingId);

    useEffect(() => {
        if (meetingError) {
            const data = meetingError.response?.data as { message?: string } | undefined;
            const message = data?.message ?? 'Erreur inconnue';
            if (message === "meetingId contains invalid characters" || message === "Meeting not found") {
                toast.error(t('vibes.meeting_not_found'));
                navigate('/', { replace: true });
            }
        }
    }, [meetingError, navigate, t]);

    const goToPrevStep = useCallback(() => {
        if (isReporting === true) return;
        const newcurrentStep = currentStep - 1;
        if (newcurrentStep >= 0) {
            setDirection(-1);
            setCurrentStep(newcurrentStep);
        }
        navigate(-1);
    }, [setDirection, setCurrentStep, currentStep, navigate, isReporting]);

    // Snapshot of the mount-time values: the initial-step redirect must only run once on mount.
    const initialRedirectRef = useRef({ step, basePath, navigate });
    useEffect(() => {
        const { step, basePath, navigate } = initialRedirectRef.current;
        if (step !== keys[0]) {
            navigate(`${basePath}/${keys[0]}`, { replace: true });
        }
    }, []);

    const severityColor = useMemo(() => [
        theme.UvibesGreen,
        theme.UvibesYellow,
        theme.UvibesOrange,
        theme.UvibesPink,
    ], [theme.UvibesGreen, theme.UvibesYellow, theme.UvibesOrange, theme.UvibesPink]);

    const leaveReport = useCallback(() => {
        if (fromHistory) {
            navigate(-(currentStep + 1));
        } else {
            navigate(`/vibes/${sessionId}/${meetingId}/end`, { state: location.state });
        }
    }, [fromHistory, currentStep, navigate, sessionId, meetingId, location.state]);

    const sendReport = useCallback(() => {
        if (isReporting === false && meetingId && selectedReportCategory) {
            report({
                meetingId: meetingId,
                message: comment,
                severity: selectedReportCategory.severity,
                reportCategoryId: selectedReportCategory.category.id
            }, {
                onSuccess: () => {
                    if (fromHistory) toast.success(t('vibes.report.success'));
                    leaveReport();
                },
                onError: (error) => {
                    const data = error.response?.data as { message?: string } | undefined;
                    const message = data?.message ?? 'Erreur inconnue'
                    if (message === "A report already exists for this meeting by this user") {
                        toast.error(t('vibes.report.already_reported'));
                        leaveReport();
                    } else {
                        toast.error(t('vibes.report.send_error'));
                    }
                }
            });
        }
    }, [selectedReportCategory, comment, report, isReporting, meetingId, fromHistory, leaveReport, t]);

    const goToNextStep = useCallback(() => {
        const newcurrentStep = currentStep + 1;
        if (newcurrentStep < keys.length) {
            setDirection(1);
            setCurrentStep(newcurrentStep);
            navigate(`${basePath}/${keys[newcurrentStep]}`);
        } else {
            sendReport();
        }

    }, [setDirection, setCurrentStep, currentStep, navigate, basePath, sendReport]);

    const currentStepIndicator = useMemo(() => (
        <div className='flex gap-3'>
            {Array.from({ length: keys.length }).map((_, index) => {
                const isSelected = currentStep === index;
                return (
                  <div
                    key={index}
                    className={`h-1.5 transition-all duration-300 ease-in-out rounded-full ${isSelected ? 'w-12.5' : 'w-3.75'}`}
                    style={{ backgroundColor: isSelected ? theme.UvibesPink : '#494949' }}
                  />
                );
            })}
        </div>
    ), [currentStep, theme.UvibesPink]);

    const handleToggleReportType = useCallback((type: string) => {
        setOpened(opened === type ? null : type);
    }, [opened, setOpened]);

    const handleSelectSeverity = useCallback((category: ReportCategoryInterface, severity: number) => {
        setSelectedReportCategory({ category, severity });
    }, [setSelectedReportCategory]);

    const chooseReportType = useMemo(() => (
        <div className='flex flex-col h-full w-full items-center justify-center'>
            <SyncLoader loading={isGettingCategories} color={theme.UvibesPink} size={24} speedMultiplier={0.5}/>
            {categoriesError && <div className="flex flex-col items-center gap-5 z-1">
                    <p className="font-semibold text-center text-sm">{t('vibes.report.categories_error')}</p>
                    <button
                        className="rounded-full text-white font-bold px-8 py-2.5 min-h-10.5 min-w-51 cursor-pointer z-1"
                        style={{ backgroundColor: theme.UvibesPink }}
                        onClick={() => refetch()}
                    >
                        {t('common.button.retry')}
                    </button>
                </div>
            }
            <div className="flex flex-col w-full h-full">
                {reportCategories && reportCategories.map((category, index) => (
                    <Fragment key={category.id}>
                        <ReportTypeItem
                            category={category}
                            isOpen={opened === category.name}
                            selectedReportCategory={selectedReportCategory}
                            severityColors={severityColor}
                            backgroundColor={theme.backgroundColor}
                            onToggle={() => handleToggleReportType(category.name)}
                            onSelectSeverity={handleSelectSeverity}
                            />
                        {index < reportCategories.length - 1 && (
                            <div className="w-full h-px" style={{ backgroundColor: '#8C8C8C' }}/>
                        )}
                    </Fragment>
                ))}
            </div>
        </div>
    ), [reportCategories, opened, selectedReportCategory, severityColor, theme.backgroundColor, handleToggleReportType, handleSelectSeverity, isGettingCategories, categoriesError, refetch, theme.UvibesPink, t]);

    const commentReport = useMemo(() => (
        <div className='flex flex-col gap-4.5'>
            <div className="flex flex-col gap-1.25">
                <h3 className="font-medium">{t('vibes.report.extra_title')}</h3>
                <p className="text-xs leading-[1.56]">{t('vibes.report.extra_description')}</p>
            </div>
            <textarea
                id="report-comment"
                placeholder={t('vibes.report.comment_placeholder')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className='w-full h-66 p-4 bg-gray-300 rounded-[10px] focus:outline-none resize-none'
            />
        </div>
    ), [setComment, comment, t]);

    const steps = useMemo(() => [
        chooseReportType,
        commentReport
    ], [chooseReportType, commentReport]);

    useEffect(() => {
        const handlePopState = () => {
            const urlParts = window.location.pathname.split('/');
            const step = urlParts[urlParts.length - 1];
            const index = keys.findIndex((key) => key === step);
            setCurrentStep((prevStep) => {
              if (index === -1) {
                return prevStep;
              }
              if (index > prevStep) {
                setDirection(1);
              } else {
                setDirection(-1);
              }
              return index;
            })
        };
    
        window.addEventListener('popstate', handlePopState);
    
        return () => {
          window.removeEventListener('popstate', handlePopState);
        };
    }, [setDirection, setCurrentStep]);

    return (
        <div
          className="min-h-dvh w-full flex flex-col gap-3 px-3 justify-between items-center pb-[3.7dvh] pt-[4.7dvh]"
          style={{ 
            background: `linear-gradient(0deg, ${theme.UvibesPinkGradient} 0%, ${theme.UvibesOrangeGradient} 75%)` 
          }}
        >
            <header className="flex flex-col items-start gap-2.5 w-full">
                {currentStep === 0 ? <CloseIcon className="absolute start-5 z-1 cursor-pointer" onClick={goToPrevStep}/> : <BackArrowIcon className="absolute start-5 z-1 cursor-pointer" onClick={goToPrevStep}/>}
                <div className="flex flex-col items-center w-full gap-3">
                    <h2 className="text-white text-xl font-medium text-center">{t('vibes.report.title')}</h2>
                    <p className="text-white text-xs leading-relaxed text-center">{t('vibes.report.subtitle')}</p>
                </div>
            </header>

            <div className="flex flex-col items-center gap-4 bg-white p-5 rounded-[30px] w-full max-w-105.5 overflow-x-hidden">
                <AnimatePresence initial={false} custom={direction} mode="wait">
                    <motion.div
                      key={currentStep}
                      custom={direction}
                      variants={PAGE_VARIANTS}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={PAGE_TRANSITION}
                      className="w-full flex flex-col justify-center min-h-91.75"
                      >
                        {steps[currentStep]}
                    </motion.div>
                </AnimatePresence>

                {currentStepIndicator}
            </div>

            <button
                className="py-2.75 px-9.75 rounded-full font-bold text-sm"
                style={selectedReportCategory !== null ? { color: theme.UvibesPink, backgroundColor: 'white' } : { color: '#494949', backgroundColor: '#858585' }}
                onClick={selectedReportCategory !== null ? goToNextStep : undefined}
            >
              {isReporting ? <PulseLoader speedMultiplier={0.75} size={8} color={theme.UvibesPink}/> : currentStep === keys.length - 1 ? t('vibes.report.send') : t('common.button.next')}
            </button>
        </div>
    );
}

export default ReportVibesPage;